import React, { useState, useEffect, useMemo } from 'react';
import {
    Button,
    Typography,
    message,
    Spin,
    Select,
    DatePicker,
    Input,
    Checkbox,
    Modal,
    Popconfirm,
    Segmented,
    Empty
} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    CalendarOutlined,
    SaveOutlined,
    DeleteOutlined,
    SwapOutlined,
    LeftOutlined,
    RightOutlined,
    FormOutlined,
    SearchOutlined,
    SettingOutlined,
    DownOutlined,
    UpOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useAttendance } from '../../hooks/useAttendance';
import { useAgeGroups } from '../../hooks/useAgeGroups';
import { useGroups } from '../../hooks/useGroups';
import useTrainers from '../../hooks/useTrainers';
import { addAttendance, deleteAttendancesByDate, moveAttendanceDate } from '../../store/slices/attendanceSlice';
import { sendWpMessage } from '../../store/slices/wpConfigSlice';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import './styles.css';

const { Text } = Typography;

const isTouchScreen = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

const DEFAULT_ABSENCE_MESSAGE = '*BİLGİLENDİRME:* Değerli Velimiz, eğitmenlerimizin aldığı yoklamaya göre çocuğunuz bugün ki futbol antrenmana katılım sağlamamıştır. Bir yanlışlık olduğunu düşünüyorsanız lütfen ilgili hocamızla iletişime geçiniz.*Spor Eğitim Atölyesi Futbol Kulübü*';

// Son 5 yoklama (en eski solda, en yeni sağda)
const AttendanceHistory = ({ attendances }) => {
    const items = [...(attendances || [])].slice(-5);
    const emptySlots = 5 - items.length;
    return (
        <div className="att-history">
            {[...Array(emptySlots)].map((_, i) => (
                <span key={`empty-${i}`} className="att-dot" title="Yoklama kaydı yok" />
            ))}
            {items.map((att, i) => (
                <span
                    key={i}
                    className={`att-dot att-dot--${att.isPresent ? 'present' : 'absent'}`}
                    title={`${new Date(att.date).toLocaleDateString('tr-TR')}: ${att.isPresent ? 'Geldi' : 'Gelmedi'}`}
                />
            ))}
        </div>
    );
};

const StudentRow = ({ student, noteOpen, onStatusChange, onNoteToggle, onNoteChange }) => {
    const rowStatus = student.status || 'unmarked';
    return (
        <div className={`att-row att-row--${rowStatus}`}>
            <div className="att-row-top">
                <div className="att-row-info">
                    <div className="att-name">{student.fullName}</div>
                    <AttendanceHistory attendances={student.last5Attendances} />
                </div>
                <div className="att-actions">
                    <Button
                        type="text"
                        className={`att-note-btn ${student.note ? 'has-note' : ''}`}
                        icon={<FormOutlined />}
                        onClick={() => onNoteToggle(student.id)}
                        aria-label="Not ekle"
                    />
                    <button
                        type="button"
                        className={`att-status-btn att-status-btn--absent ${student.status === 'absent' ? 'is-active' : ''}`}
                        onClick={() => onStatusChange(student.id, 'absent')}
                        aria-pressed={student.status === 'absent'}
                    >
                        <CloseOutlined />
                        <span className="att-status-label">Yok</span>
                    </button>
                    <button
                        type="button"
                        className={`att-status-btn att-status-btn--present ${student.status === 'present' ? 'is-active' : ''}`}
                        onClick={() => onStatusChange(student.id, 'present')}
                        aria-pressed={student.status === 'present'}
                    >
                        <CheckOutlined />
                        <span className="att-status-label">Var</span>
                    </button>
                </div>
            </div>
            {noteOpen && (
                <Input
                    className="att-note"
                    placeholder="Not ekle..."
                    value={student.note}
                    onChange={(e) => onNoteChange(student.id, e.target.value)}
                    allowClear
                />
            )}
        </div>
    );
};

const Attendance = () => {
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const { authorities } = useSelector((state) => state.auth);
    const isAdmin = !(authorities || []).includes('Trainer');
    const {
        students,
        loading,
        fetchStudentsByAgeGroup,
        fetchStudentsByGroup,
        clearStudentsList,
        clearAttendancesList
    } = useAttendance();
    const { ageGroups } = useAgeGroups(selectedBranch?.id);
    const { groups } = useGroups(selectedBranch?.id);
    const { trainers } = useTrainers();

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [filterType, setFilterType] = useState('group'); // 'ageGroup' veya 'group'
    const [attendanceData, setAttendanceData] = useState([]);
    const [saving, setSaving] = useState(false);
    const [sendAbsenceMsg, setSendAbsenceMsg] = useState(true);
    const [absenceMessage, setAbsenceMessage] = useState(DEFAULT_ABSENCE_MESSAGE);
    const [openNotes, setOpenNotes] = useState({});
    const [searchText, setSearchText] = useState('');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [moveTargetDate, setMoveTargetDate] = useState(null);
    const [attendanceActionLoading, setAttendanceActionLoading] = useState(false);

    const selectedScopeId = filterType === 'group' ? selectedGroup : selectedAgeGroup;

    // Tarih veya grup değiştiğinde öğrencileri getir
    useEffect(() => {
        if (selectedDate && selectedBranch) {
            if (filterType === 'ageGroup' && selectedAgeGroup) {
                fetchStudentsByAgeGroup(selectedAgeGroup, selectedDate);
            } else if (filterType === 'group' && selectedGroup) {
                fetchStudentsByGroup(selectedGroup, selectedDate);
            } else {
                setAttendanceData([]);
                clearStudentsList();
                clearAttendancesList();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, selectedAgeGroup, selectedGroup, filterType, selectedBranch]);

    // Öğrenci listesi geldiğinde yoklama durumlarını hazırla.
    // O gün kaydı olmayan öğrenci "işaretlenmedi" başlar; yanlışlıkla herkes yok kaydedilmesin.
    useEffect(() => {
        if (students && students.length > 0) {
            setAttendanceData(students.map(student => {
                const savedStatus = student.attendanceDate ? (student.isPresent ? 'present' : 'absent') : null;
                return {
                    id: student.id,
                    fullName: student.fullName,
                    status: savedStatus,
                    savedStatus,
                    note: student.note || '',
                    attendanceDate: student.attendanceDate || null,
                    last5Attendances: student.last5Attendances || []
                };
            }));
            const initialNotes = {};
            students.forEach(student => {
                initialNotes[student.id] = !!student.note;
            });
            setOpenNotes(initialNotes);
        } else {
            setAttendanceData([]);
            setOpenNotes({});
        }
    }, [students]);

    const counts = useMemo(() => ({
        present: attendanceData.filter(s => s.status === 'present').length,
        absent: attendanceData.filter(s => s.status === 'absent').length,
        unmarked: attendanceData.filter(s => !s.status).length
    }), [attendanceData]);

    const hasSavedAttendance = attendanceData.some(student => student.attendanceDate);

    const visibleStudents = useMemo(() => {
        const q = searchText.trim().toLocaleLowerCase('tr-TR');
        if (!q) return attendanceData;
        return attendanceData.filter(s => s.fullName?.toLocaleLowerCase('tr-TR').includes(q));
    }, [attendanceData, searchText]);

    const resetList = () => {
        clearStudentsList();
        clearAttendancesList();
        setAttendanceData([]);
        setOpenNotes({});
        setSearchText('');
    };

    const handleFilterTypeChange = (type) => {
        setFilterType(type);
        setSelectedAgeGroup(null);
        setSelectedGroup(null);
        resetList();
    };

    const handleScopeChange = (id) => {
        if (filterType === 'group') {
            setSelectedGroup(id ?? null);
            setSelectedAgeGroup(null);
        } else {
            setSelectedAgeGroup(id ?? null);
            setSelectedGroup(null);
        }
        setAttendanceData([]);
        setOpenNotes({});
        setSearchText('');
    };

    const handleStatusChange = (studentId, status) => {
        setAttendanceData(prev => prev.map(s => (s.id === studentId ? { ...s, status } : s)));
    };

    const handleNoteChange = (studentId, note) => {
        setAttendanceData(prev => prev.map(s => (s.id === studentId ? { ...s, note } : s)));
    };

    const toggleNote = (studentId) => {
        setOpenNotes(prev => ({ ...prev, [studentId]: !prev[studentId] }));
    };

    const markAll = (status, onlyUnmarked = false) => {
        setAttendanceData(prev => prev.map(s => (onlyUnmarked && s.status ? s : { ...s, status })));
    };

    const refreshStudents = () => {
        if (filterType === 'ageGroup' && selectedAgeGroup) {
            fetchStudentsByAgeGroup(selectedAgeGroup, selectedDate);
        } else if (filterType === 'group' && selectedGroup) {
            fetchStudentsByGroup(selectedGroup, selectedDate);
        }
    };

    const getAttendanceScope = () => ({
        branchId: selectedBranch.id,
        groupId: filterType === 'group' ? selectedGroup : null,
        ageGroupId: filterType === 'ageGroup' ? selectedAgeGroup : null
    });

    const handleSaveAttendance = async () => {
        if (attendanceData.length === 0) {
            message.warning('Kaydedilecek yoklama verisi bulunmamaktadır!');
            return;
        }
        if (counts.unmarked > 0) {
            message.warning(`${counts.unmarked} öğrenci henüz işaretlenmedi!`);
            return;
        }

        setSaving(true);
        try {
            await dispatch(addAttendance({
                trainerId: selectedTrainer || null,
                date: selectedDate.format('YYYY-MM-DD'),
                studentAttendanceDtos: attendanceData.map(student => ({
                    studentId: student.id,
                    isPresent: student.status === 'present',
                    note: student.note || null
                }))
            })).unwrap();
            message.success('Yoklama başarıyla kaydedildi!');
        } catch (error) {
            console.error('Save attendance error:', error);
            message.error(`Yoklama kaydedilirken bir hata oluştu: ${error?.message || error}`);
            setSaving(false);
            return;
        }

        // Veli mesajı sadece yeni "yok" olanlara gider; yoklama tekrar kaydedilince aynı veliye ikinci mesaj gitmez.
        // Mesaj hatası yoklamanın kaydedildiği bilgisini gölgelemesin diye ayrı ele alınır.
        if (sendAbsenceMsg) {
            const newlyAbsentIds = attendanceData
                .filter(s => s.status === 'absent' && s.savedStatus !== 'absent')
                .map(s => s.id);
            if (newlyAbsentIds.length > 0) {
                try {
                    await dispatch(sendWpMessage({
                        studentIds: newlyAbsentIds,
                        message: absenceMessage
                    })).unwrap();
                    message.success(`${newlyAbsentIds.length} öğrencinin velisine mesaj gönderildi!`);
                } catch (error) {
                    message.warning(`Yoklama kaydedildi ancak veli mesajı gönderilemedi: ${error?.message || error}`);
                }
            }
        }

        refreshStudents();
        setSaving(false);
    };

    const handleDeleteAttendance = async () => {
        setAttendanceActionLoading(true);
        try {
            const result = await dispatch(deleteAttendancesByDate({
                ...getAttendanceScope(),
                date: selectedDate.format('YYYY-MM-DD')
            })).unwrap();
            message.success(`${selectedDate.format('DD/MM/YYYY')} tarihli yoklama silindi (${result?.deletedCount ?? 0} kayıt).`);
            refreshStudents();
        } catch (error) {
            message.error(`Yoklama silinirken bir hata oluştu: ${error?.message || error}`);
        } finally {
            setAttendanceActionLoading(false);
        }
    };

    const handleMoveAttendance = async () => {
        if (!moveTargetDate) {
            message.warning('Lütfen yeni tarihi seçin!');
            return;
        }
        if (moveTargetDate.isSame(selectedDate, 'day')) {
            message.warning('Yeni tarih mevcut tarihten farklı olmalıdır!');
            return;
        }

        setAttendanceActionLoading(true);
        try {
            const result = await dispatch(moveAttendanceDate({
                ...getAttendanceScope(),
                fromDate: selectedDate.format('YYYY-MM-DD'),
                toDate: moveTargetDate.format('YYYY-MM-DD')
            })).unwrap();
            message.success(`Yoklama ${moveTargetDate.format('DD/MM/YYYY')} tarihine taşındı (${result?.movedCount ?? 0} kayıt).`);
            setMoveModalOpen(false);
            // Yeni tarihe geç; öğrenci listesi effect ile yeniden yüklenir
            setSelectedDate(moveTargetDate);
            setMoveTargetDate(null);
        } catch (error) {
            message.error(`Yoklama tarihi değiştirilirken bir hata oluştu: ${error?.message || error}`);
        } finally {
            setAttendanceActionLoading(false);
        }
    };

    const dateCaption = () => {
        const today = dayjs();
        const dayName = selectedDate.locale('tr').format('dddd');
        if (selectedDate.isSame(today, 'day')) return `Bugün · ${dayName}`;
        if (selectedDate.isSame(today.subtract(1, 'day'), 'day')) return `Dün · ${dayName}`;
        if (selectedDate.isSame(today.add(1, 'day'), 'day')) return `Yarın · ${dayName}`;
        return dayName;
    };

    if (!selectedBranch) {
        return (
            <div className="att-page">
                <div className="att-card att-empty">
                    <h3>Lütfen önce bir branş seçiniz</h3>
                    <p>Yoklama almak için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                </div>
            </div>
        );
    }

    const scopeOptions = (filterType === 'group' ? groups : ageGroups).map(item => ({
        value: item.id,
        label: item.name
    }));

    return (
        <div className="att-page">
            {/* Tarih ve grup seçimi */}
            <div className="att-card">
                <div className="att-date-nav">
                    <Button
                        className="att-icon-btn"
                        icon={<LeftOutlined />}
                        onClick={() => setSelectedDate(d => d.subtract(1, 'day'))}
                        aria-label="Önceki gün"
                    />
                    <DatePicker
                        value={selectedDate}
                        onChange={(date) => date && setSelectedDate(date)}
                        format="DD/MM/YYYY"
                        allowClear={false}
                        inputReadOnly
                        suffixIcon={<CalendarOutlined />}
                    />
                    <Button
                        className="att-icon-btn"
                        icon={<RightOutlined />}
                        onClick={() => setSelectedDate(d => d.add(1, 'day'))}
                        aria-label="Sonraki gün"
                    />
                </div>
                <div className="att-date-caption">
                    {dateCaption()}
                    {!selectedDate.isSame(dayjs(), 'day') && (
                        <Button type="link" size="small" onClick={() => setSelectedDate(dayjs())}>
                            Bugüne dön
                        </Button>
                    )}
                </div>
                <div className="att-filter-row">
                    <Segmented
                        value={filterType}
                        onChange={handleFilterTypeChange}
                        options={[
                            { label: 'Grup', value: 'group' },
                            { label: 'Yaş Grubu', value: 'ageGroup' }
                        ]}
                    />
                    <Select
                        value={selectedScopeId}
                        onChange={handleScopeChange}
                        placeholder={filterType === 'group' ? 'Grup seçin' : 'Yaş grubu seçin'}
                        options={scopeOptions}
                        allowClear
                        showSearch={!isTouchScreen()}
                        optionFilterProp="label"
                    />
                </div>
            </div>

            {/* Kayıtlı yoklama işlemleri (yanlış güne girilen yoklamayı taşıma / silme) */}
            {isAdmin && hasSavedAttendance && (
                <div className="att-card att-saved">
                    <span className="att-saved-text">
                        <CalendarOutlined /> Bu gün için kayıtlı yoklama var
                    </span>
                    <div className="att-saved-actions">
                        <Button
                            size="small"
                            icon={<SwapOutlined />}
                            onClick={() => {
                                setMoveTargetDate(null);
                                setMoveModalOpen(true);
                            }}
                            disabled={attendanceActionLoading}
                        >
                            Tarihi Değiştir
                        </Button>
                        <Popconfirm
                            title="Yoklamayı sil"
                            description={`${selectedDate.format('DD/MM/YYYY')} tarihli yoklama bu grup için tamamen silinecek. Emin misiniz?`}
                            onConfirm={handleDeleteAttendance}
                            okText="Sil"
                            cancelText="Vazgeç"
                            okButtonProps={{ danger: true }}
                        >
                            <Button size="small" danger icon={<DeleteOutlined />} loading={attendanceActionLoading}>
                                Sil
                            </Button>
                        </Popconfirm>
                    </div>
                </div>
            )}

            {attendanceData.length > 0 ? (
                <>
                    {/* Özet ve toplu işlemler */}
                    <div className="att-card att-toolbar">
                        <div className="att-counts">
                            <div className="att-count att-count--present"><b>{counts.present}</b><span>Var</span></div>
                            <div className="att-count att-count--absent"><b>{counts.absent}</b><span>Yok</span></div>
                            <div className="att-count att-count--unmarked"><b>{counts.unmarked}</b><span>İşaretsiz</span></div>
                        </div>
                        <div className="att-bulk">
                            {counts.unmarked > 0 && counts.unmarked < attendanceData.length && (
                                <Button size="small" className="att-bulk-rest" icon={<CheckOutlined />} onClick={() => markAll('present', true)}>
                                    Kalanlar Var
                                </Button>
                            )}
                            <Button size="small" icon={<CheckOutlined />} onClick={() => markAll('present')}>
                                Hepsi Var
                            </Button>
                            <Button size="small" danger icon={<CloseOutlined />} onClick={() => markAll('absent')}>
                                Hepsi Yok
                            </Button>
                        </div>
                        {attendanceData.length > 8 && (
                            <Input
                                className="att-search"
                                prefix={<SearchOutlined />}
                                placeholder="Öğrenci ara..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                            />
                        )}
                    </div>

                    {/* Öğrenci listesi */}
                    <div className="att-card att-list">
                        {visibleStudents.length > 0 ? (
                            visibleStudents.map(student => (
                                <StudentRow
                                    key={student.id}
                                    student={student}
                                    noteOpen={!!openNotes[student.id]}
                                    onStatusChange={handleStatusChange}
                                    onNoteToggle={toggleNote}
                                    onNoteChange={handleNoteChange}
                                />
                            ))
                        ) : (
                            <div className="att-empty">
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aramaya uyan öğrenci yok" />
                            </div>
                        )}
                    </div>

                    {/* Kaydet */}
                    <div className="att-card att-footer">
                        <Button
                            type="link"
                            className="att-settings-toggle"
                            icon={<SettingOutlined />}
                            onClick={() => setSettingsOpen(o => !o)}
                        >
                            Eğitmen ve veli mesajı {settingsOpen ? <UpOutlined /> : <DownOutlined />}
                        </Button>
                        {settingsOpen && (
                            <div className="att-settings">
                                <Select
                                    value={selectedTrainer}
                                    onChange={setSelectedTrainer}
                                    placeholder="Eğitmen seçin (opsiyonel)"
                                    allowClear
                                    showSearch={!isTouchScreen()}
                                    optionFilterProp="label"
                                    options={trainers.map(t => ({ value: t.id, label: t.fullName }))}
                                />
                                <Checkbox checked={sendAbsenceMsg} onChange={e => setSendAbsenceMsg(e.target.checked)}>
                                    Yok olanların velisine mesaj gönder
                                </Checkbox>
                                {sendAbsenceMsg && (
                                    <Input.TextArea
                                        value={absenceMessage}
                                        onChange={(e) => setAbsenceMessage(e.target.value)}
                                        placeholder="Yok olan öğrencilere gönderilecek mesaj..."
                                        autoSize={{ minRows: 2, maxRows: 6 }}
                                        style={{ gridColumn: '1 / -1' }}
                                    />
                                )}
                            </div>
                        )}
                        <Button
                            type="primary"
                            className="att-save-btn"
                            icon={<SaveOutlined />}
                            onClick={handleSaveAttendance}
                            loading={saving}
                            disabled={counts.unmarked > 0}
                        >
                            {saving ? 'Kaydediliyor...' : `Yoklamayı Kaydet (${counts.present} Var / ${counts.absent} Yok)`}
                        </Button>
                        {counts.unmarked > 0 && (
                            <div className="att-save-hint">
                                Kaydetmek için {counts.unmarked} öğrenciyi daha işaretleyin
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="att-card att-empty">
                    {loading ? (
                        <Spin size="large" />
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={selectedScopeId ? 'Bu grupta öğrenci bulunmamaktadır' : 'Yoklama almak için grup seçin'}
                        />
                    )}
                </div>
            )}

            <Modal
                title="Yoklama Tarihini Değiştir"
                open={moveModalOpen}
                onOk={handleMoveAttendance}
                onCancel={() => setMoveModalOpen(false)}
                okText="Taşı"
                cancelText="Vazgeç"
                confirmLoading={attendanceActionLoading}
            >
                <Text>
                    <Text strong>{selectedDate.format('DD/MM/YYYY')}</Text> tarihli yoklama seçilen yeni tarihe taşınacak.
                </Text>
                <DatePicker
                    value={moveTargetDate}
                    onChange={setMoveTargetDate}
                    format="DD/MM/YYYY"
                    placeholder="Yeni tarih"
                    style={{ width: '100%', marginTop: 12 }}
                    inputReadOnly
                />
            </Modal>
        </div>
    );
};

export default Attendance;
